'use client';

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { socketIo } from '@/contexts/NotificationProvider';
import {
  getAePreparedNotifications,
  markAePreparedNotificationRead,
  markAllAePreparedNotificationsRead,
} from '@/services/aeNotifications';

export const AE_PREPARED_NOTIFICATIONS_QUERY_KEY = 'ae-prepared-notifications';

export const useAePreparedNotifications = (
  enabled: boolean,
  listenForPush = false,
  workspaceId: number | null = null,
  // AEs poll for prepared work; everyone else relies on the live push and a read on open (loop
  // heads-ups arrive once a month, not worth a request every 30 seconds per signed-in user).
  poll = true,
) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    // Keep cached notifications isolated when an admin switches workspaces.
    // The API is tenant-scoped too, but the browser must never briefly render
    // a prior workspace's cached notification while the new request loads.
    queryKey: [AE_PREPARED_NOTIFICATIONS_QUERY_KEY, workspaceId],
    queryFn: getAePreparedNotifications,
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled && poll ? 30_000 : false,
  });

  useEffect(() => {
    if (!enabled || !listenForPush) return undefined;
    const handlePrepared = () => {
      queryClient.invalidateQueries({ queryKey: [AE_PREPARED_NOTIFICATIONS_QUERY_KEY, workspaceId] });
    };
    socketIo.on('user_notification', handlePrepared);
    return () => { socketIo.off('user_notification', handlePrepared); };
  }, [enabled, listenForPush, queryClient, workspaceId]);

  return query;
};

export const useMarkAePreparedNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAePreparedNotificationRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [AE_PREPARED_NOTIFICATIONS_QUERY_KEY] }),
  });
};

export const useMarkAllAePreparedNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markAllAePreparedNotificationsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [AE_PREPARED_NOTIFICATIONS_QUERY_KEY] }),
  });
};
